// Adapted from herdr voice.ts, Copyright (c) 2026 devswha (MIT).
import { ApiError } from '@pen/shared';
import { ApiRequestError } from '../api';
import { createSpeechGate, levelFromRms, MAX_AUDIO_BYTES, pickRecorderMime, readVoiceEvents, smoothLevel, SPEECH_PREROLL_MS, type VoiceText } from './helpers';
import { refreshVoiceStatus, type VoiceStatusValue } from './status';

export interface Utterance { text: string; t: number; t1: number }
export interface VoiceOptions {
  lessonId?: string;
  now(): number;
  continuous: boolean;
  onUtterance?(utterance: Utterance): void | Promise<void>;
  onText?(result: VoiceText): void;
}
export interface VoiceState { active: boolean; starting: boolean; busy: boolean; error: string | null; lastText: string }
interface Segment { recorder: MediaRecorder; chunks: Blob[]; t: number; t1: number; at: number; id: number; stopped: Promise<void>; finish(): void }
interface Run {
  options: VoiceOptions;
  ended: boolean;
  stream: MediaStream | null;
  context: AudioContext | null;
  source: MediaStreamAudioSourceNode | null;
  delay: DelayNode | null;
  destination: MediaStreamAudioDestinationNode | null;
  segment: Segment | null;
  frame: number;
  openedAt: number;
  stop: Promise<void> | null;
}
const ERROR_NOTES: Record<string, string> = {
  insecure: 'Voice input needs HTTPS.', permission: 'Microphone permission was denied.', no_mic: 'No microphone was found.',
  unsupported: 'This browser cannot record audio.', not_configured: 'OpenRouter voice is not configured. Open voice settings to add a key.',
  provider_auth: 'The OpenRouter key was refused.', provider: 'Transcription failed.', network: 'The transcription connection failed.',
  too_large: 'The recording exceeded 8 MiB.', no_speech: 'No speech was heard.', rate_limited: 'Voice transcription is rate limited. Try again shortly.',
};
/** Imperative capture boundary: no React frame updates, fresh container per utterance, ordered delivery. */
export function createVoiceController(io: {
  options(): VoiceOptions;
  status(): VoiceStatusValue | null;
  meter(): HTMLElement | null;
  changed(state: VoiceState): void;
}) {
  let current: Run | null = null;
  let serial: Promise<void> = Promise.resolve();
  let nextId = 0;
  let state: VoiceState = { active: false, starting: false, busy: false, error: null, lastText: '' };
  function update(patch: Partial<VoiceState>): void { state = { ...state, ...patch }; io.changed(state); }
  function fail(code: string): void { update({ error: ERROR_NOTES[code] ?? ERROR_NOTES.provider! }); }
  function enqueue(work: () => Promise<void>): void {
    update({ busy: true });
    const job = serial.then(work).catch((error: unknown) => {
      if (error instanceof ApiRequestError) fail(error.code);
      else fail(error instanceof TypeError ? 'network' : 'provider');
    });
    serial = job;
    void job.then(() => { if (serial === job) update({ busy: false }); });
  }
  async function transcribe(run: Run, segment: Segment): Promise<void> {
    const blob = new Blob(segment.chunks, { type: segment.recorder.mimeType });
    if (!blob.size) return;
    if (blob.size > MAX_AUDIO_BYTES) { fail('too_large'); return; }
    const query = new URLSearchParams();
    if (run.options.lessonId) query.set('lessonId', run.options.lessonId);
    query.set('polish', '1');
    const response = await fetch(`/api/voice/transcribe?${query}`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': blob.type }, body: blob,
    });
    const ndjson = response.headers.get('content-type')?.includes('application/x-ndjson') === true;
    if (!response.body || (!response.ok && !ndjson)) {
      const parsed = ApiError.safeParse(await response.json().catch(() => null));
      if (response.status === 401) window.dispatchEvent(new Event('pen:unauthenticated'));
      fail(parsed.success ? parsed.data.error : response.status === 413 ? 'too_large' : 'provider');
      return;
    }
    let raw = '', cleaned = '';
    let done = false;
    try {
      for await (const event of readVoiceEvents(response.body)) {
        if (event.type === 'error') { fail(event.code); continue; }
        if (event.type === 'done') { done = true; break; }
        const text = event.text.trim();
        if (!text) continue;
        if (event.phase === 'raw') raw = text;else cleaned = text;
        update({ lastText: text });
        if (!run.options.continuous) run.options.onText?.({ text, phase: event.phase, take: segment.id });
      }
    } finally {
      // A provider/polish failure must not lose the words already received.
      if (run.options.continuous && raw) await run.options.onUtterance?.({ text: cleaned || raw, t: segment.t, t1: segment.t1 });
    }
    if (!done) fail('network');
  }
  function openSegment(run: Run, mimeType: string, t: number): void {
    const recorder = new MediaRecorder(run.destination!.stream, { mimeType });
    let finish!: () => void;
    const segment: Segment = {
      recorder, chunks: [], t, t1: t, at: performance.now(), id: ++nextId,
      stopped: new Promise<void>((resolve) => { finish = resolve; }), finish: () => finish(),
    };
    recorder.ondataavailable = (event) => { if (event.data.size) segment.chunks.push(event.data); };
    recorder.onstop = () => segment.finish();
    recorder.onerror = () => { fail('provider'); void stop(); };
    recorder.start(1000);
    run.segment = segment;
  }
  function closeSegment(run: Run, t1: number): Promise<void> {
    const segment = run.segment;
    if (!segment) return Promise.resolve();
    run.segment = null;
    segment.t1 = Math.max(segment.t, t1);
    // Enqueue at the boundary, not onstop: recorder event timing cannot reorder utterances.
    enqueue(async () => { await segment.stopped; await transcribe(run, segment); });
    if (segment.recorder.state !== 'inactive') segment.recorder.stop();
    return segment.stopped;
  }
  async function beginServer(run: Run, opening: Promise<MediaStream>, resumed: Promise<void>, status: Promise<VoiceStatusValue>): Promise<void> {
    try {
      // Install the stream immediately, so a later failure/status response can always release it.
      const stream = await opening;
      if (run.ended) { stream.getTracks().forEach((track) => track.stop()); return; }
      run.stream = stream;
      const configured = await status;
      if (run.ended) return;
      if (configured.engine !== 'server' || !configured.hasKey) { fail('not_configured');await stop();return; }
      await resumed;
      if (run.ended) return;
      const context = run.context!;
      const mimeType = typeof MediaRecorder === 'undefined' ? null : pickRecorderMime((mime) => MediaRecorder.isTypeSupported(mime));
      if (!mimeType) { fail('unsupported'); await stop(); return; }
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      const samples = new Float32Array(analyser.fftSize);
      const source = context.createMediaStreamSource(stream);
      const delay = context.createDelay(1);
      delay.delayTime.value = SPEECH_PREROLL_MS / 1000;
      const destination = context.createMediaStreamDestination();
      source.connect(analyser);
      source.connect(delay);
      delay.connect(destination);
      run.source = source;
      run.delay = delay;
      run.destination = destination;
      run.openedAt = run.options.now();
      const gate = createSpeechGate();
      let level = 0;
      let previous = performance.now();
      const frame = (at: number): void => {
        if (run.ended) return;
        analyser.getFloatTimeDomainData(samples);
        let sum = 0;
        for (const sample of samples) sum += sample * sample;
        const rms = Math.sqrt(sum / samples.length);
        level = smoothLevel(level, levelFromRms(rms), at - previous);
        previous = at;
        io.meter()?.style.setProperty('--voice-level', level.toFixed(3));
        const change = gate.step(rms, at);
        const now = run.options.now();
        try {
          if (change === 'open') openSegment(run, mimeType, Math.max(run.openedAt, now - SPEECH_PREROLL_MS));
          else if (change === 'close') void closeSegment(run, now - SPEECH_PREROLL_MS);
          // Each restart creates its own container header, never a slice of a streaming WebM/MP4.
          else if (run.segment && at - run.segment.at >= 45_000) {
            void closeSegment(run, now - SPEECH_PREROLL_MS);
            openSegment(run, mimeType, Math.max(run.openedAt, now - SPEECH_PREROLL_MS));
          }
        } catch { fail('provider'); void stop(); return; }
        run.frame = requestAnimationFrame(frame);
      };
      run.frame = requestAnimationFrame(frame);
      stream.getAudioTracks().forEach((track) => track.addEventListener('ended', () => { if (!run.ended) void stop(); }, { once: true }));
      update({ starting: false });
    } catch (error) {
      if (run.ended) return;
      fail(error instanceof DOMException ? error.name === 'NotAllowedError' || error.name === 'SecurityError' ? 'permission' : 'no_mic' : 'provider');
      await stop();
    }
  }
  function start(): void {
    if (current || state.busy) return;
    if (!window.isSecureContext) { fail('insecure'); return; }
    const run: Run = {
      options: io.options(), ended: false, stream: null, context: null, source: null, delay: null, destination: null,
      segment: null, frame: 0, openedAt: 0, stop: null,
    };
    current = run;
    update({ active: true, starting: true, error: null, lastText: '' });
    const status = io.status();
    try {
      if (status && (status.engine !== 'server' || !status.hasKey)) { fail('not_configured');void stop();return; }
      if (!navigator.mediaDevices?.getUserMedia) { fail('no_mic'); void stop(); return; }
      // Safari's prefixed constructor is a browser boundary absent from lib.dom.
      const audioWindow = window as unknown as { webkitAudioContext?: typeof AudioContext };
      const Audio = window.AudioContext ?? audioWindow.webkitAudioContext;
      if (!Audio) { fail('unsupported'); void stop(); return; }
      // Both privileged calls happen synchronously in pointerdown/click, before any await.
      run.context = new Audio();
      const resumed = run.context.resume();
      void resumed.catch(() => undefined);
      const opening = navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      const known = status ? Promise.resolve(status) : refreshVoiceStatus();
      void known.catch(() => undefined);
      void beginServer(run, opening, resumed, known);
    } catch { fail('unsupported'); void stop(); }
  }
  function stop(): Promise<void> {
    const run = current;
    if (!run) return serial;
    if (run.stop) return run.stop;
    run.ended = true;
    if (run.frame) cancelAnimationFrame(run.frame);
    run.source?.disconnect();
    run.stream?.getTracks().forEach((track) => track.stop());
    io.meter()?.style.setProperty('--voice-level', '0');
    update({ active: false, starting: false, busy: true });
    const t1 = run.options.now();
    run.stop = (async () => {
      try {
        if (run.segment) {
          // The physical mic is already stopped; let its delayed last syllable reach the recorder.
          await new Promise<void>((resolve) => setTimeout(resolve, SPEECH_PREROLL_MS));
          await closeSegment(run, t1);
        }
        await serial;
      } finally {
        run.delay?.disconnect();
        run.destination?.stream.getTracks().forEach((track) => track.stop());
        await run.context?.close().catch(() => undefined);
        if (current === run) current = null;
        update({ busy: false });
      }
    })();
    return run.stop;
  }
  return { start, stop, toggle: () => { if (current) void stop(); else start(); } };
}
